import { faCircleExclamationCheck } from "@fortawesome/pro-regular-svg-icons/faCircleExclamationCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { AssessmentInstanceDto, AssessmentInstanceResultDto, AssessmentPlayerMode, AssessmentType } from "generated/CoreApi_v1";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssPlayerImage } from "views/assessment/player/common/AssPlayerImage";
import { AssPlayerTitleHeader } from "views/assessment/player/common/AssPlayerTitleHeader";

/////////////////////////////////////////////////////////////////////////////////

interface IProps {
    viewData: AssessmentInstanceDto;
    resultData: AssessmentInstanceResultDto;
    fullScreenMode: boolean;
}

export const AssPlayerConclusionBox: React.FC<IProps> = (props) => {

    const { viewData, resultData, fullScreenMode } = props;

    const theme = useTheme();
    const { t } = useTranslation("assessment");

    const [clientHeight, setClientHeight] = useState<number>(0);

    const titleRef = useRef<any>(undefined);

    useEffect(() => {
        setClientHeight(titleRef.current.clientHeight + 16);
    }, [fullScreenMode]);

    ///
    const RenderEvaluation = () => {

        let percentual = resultData.totalPoints && resultData.totalPoints > 0 ? resultData.totalAnswerPoints / resultData.totalPoints * 100 : 0;
        const color = resultData.hasEssay || resultData.minFinalScore == null || resultData.minFinalScore == 0
            ? theme.palette.muted.main
            : resultData.minFinalScore > percentual
                ? theme.palette.warning.main
                : theme.palette.success.main;

        return (
            <Grid container direction="column" spacing={2}>
                <Grid>
                    <Grid container alignItems="center" spacing={3}>
                        <Grid>
                            <FontAwesomeIcon
                                size="5x"
                                color={color}
                                fixedWidth
                                icon={faCircleExclamationCheck}
                            />
                        </Grid>
                        {
                            resultData.hasEssay
                                ?
                                <Grid textAlign="center" sx={{ textTransform: 'uppercase' }}>
                                    <Typography variant="h4" component="p" color={color}>
                                        {t("AssPlayer.Conclusion.WaitTestScoreText")}
                                    </Typography>
                                </Grid>
                                :
                                <Grid textAlign="center" sx={{ textTransform: 'uppercase' }}>
                                    <Typography variant="h1" component="p" color={color}>
                                        {percentual.toFixed(0)}%
                                    </Typography>
                                    <Typography variant="h4" component="p" color={color}>
                                        {t("AssPlayer.Conclusion.SuccessText")}
                                    </Typography>
                                </Grid>
                        }
                    </Grid>
                </Grid>
                <Grid>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.notice.main,
                            padding: 2,
                            borderRadius: "8px",
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: theme.palette.muted.main
                        }}
                    >
                        {
                            resultData.timeOver &&
                            <Typography variant="subtitle1" component="p" color={theme.palette.muted.main} flexWrap="wrap">
                                <strong>{t("AssPlayer.Conclusion.TimeOverText")}</strong>
                            </Typography>
                        }
                        <Typography variant="subtitle1" component="p" color={theme.palette.muted.main} flexWrap="wrap">
                            {
                                resultData.hasEssay
                                    ?
                                    t("AssPlayer.Conclusion.HasEssayMessage")
                                    :
                                    t("AssPlayer.Conclusion.MessageText", {
                                        val: resultData.totalAnswerPoints,
                                        total: resultData.totalPoints,
                                        percentual: percentual.toFixed(0)
                                    })
                            }
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        );
    };

    ///
    const RenderSurvey = () => {
        return (
            <>
                {
                    viewData.playerMode == AssessmentPlayerMode.UserReview || viewData.playerMode == AssessmentPlayerMode.FinalResult
                        ?
                        <Grid>
                            <Grid textAlign="center" sx={{ textTransform: 'uppercase' }}>
                                <Typography variant="h2" component="p" color={theme.palette.muted.main} >
                                    {t("AssPlayer.Conclusion.EndResultsText")}
                                </Typography>
                            </Grid>
                        </Grid>
                        :
                        <Grid>
                            <Grid textAlign="center" sx={{ textTransform: 'uppercase' }}>
                                <Typography variant="h2" component="p" color={theme.palette.success.main} >
                                    {t("AssPlayer.Conclusion.SurveyFinishMessageL1")}
                                </Typography>
                            </Grid>
                            <Grid textAlign="center" sx={{ textTransform: 'uppercase' }}>
                                <Typography variant="h2" component="p" color={theme.palette.success.main} >
                                    {t("AssPlayer.Conclusion.SurveyFinishMessageL2")}
                                </Typography>
                            </Grid>
                        </Grid>
                }
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <AssPlayerTitleHeader title={viewData.title} ref={titleRef} />

            <Box sx={{
                height: `calc(${fullScreenMode ? "78vh" : "52vh"} - ${clientHeight}px)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
                <Grid container direction="column" spacing={2}>
                    <Grid size="grow">
                        <Grid container justifyContent="center" spacing={2}>
                            <Grid>
                                <AssPlayerImage viewData={viewData} />
                            </Grid>
                            <Grid size="grow">
                                {
                                    viewData.assessmentType == AssessmentType.Evaluation
                                        ? RenderEvaluation()
                                        : viewData.assessmentType == AssessmentType.Survey
                                            ? RenderSurvey()
                                            : <Box></Box>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
